import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, Folder, Book, Users, History, Settings, ShieldCheck, CreditCard, ChevronRight, LogOut } from 'lucide-react';
import * as AuthModule from 'modules/auth';
import { useFeature, FEATURES } from 'modules/entitlements';
import { PERMISSIONS } from 'modules/auth/permissions';
import { useSettingsDialog } from 'components/settings/useSettingsDialog';
import type { SettingsSection } from 'components/settings/registry';
import { MobileScreen, ScreenHeader, ScreenBody, M } from 'components/mobile';

interface MoreItem {
  key: string;
  icon: React.ElementType;
  label: string;
  sub: string;
  show: boolean;
  onClick: () => void;
}

/** Each row routes to the real destination, permission-gated. Sits behind the dock's "More" tab. */
export function MobileMore() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open: openSettings } = useSettingsDialog();
  const {
    state: { permissions: p, organization, user },
    methods,
  } = AuthModule.Context.useAuthContext();
  const hasCollections = useFeature(FEATURES.COLLECTIONS);

  const has = (perm: string) => p.includes(perm as any);
  const goSettings = (section: SettingsSection) => openSettings(section);

  const items: MoreItem[] = [
    {
      key: 'documents',
      icon: Sparkles,
      label: t('nav.ai_generate', 'AI Generate'),
      sub: t('more_documents_sub', 'Upload sources, auto-generate quizzes'),
      show: has(PERMISSIONS.DOCUMENTS_EDIT),
      onClick: () => navigate('/app/documents'),
    },
    {
      key: 'library',
      icon: Folder,
      label: t('nav.library', 'Library'),
      sub: t('more_library_sub', 'Folders and shared collections'),
      show: hasCollections && has(PERMISSIONS.COLLECTIONS_LIST),
      onClick: () => navigate('/app/library'),
    },
    {
      key: 'subjects',
      icon: Book,
      label: t('nav.subjects', 'Subjects'),
      sub: t('more_subjects_sub', 'Curriculum subjects & topics'),
      show: has(PERMISSIONS.SUBJECTS_VIEW),
      onClick: () => navigate('/app/subjects'),
    },
    {
      key: 'members',
      icon: Users,
      label: t('nav.members', 'Members'),
      sub: t('more_members_sub', 'Org-wide roster & roles'),
      show: has(PERMISSIONS.MEMBERS_EDIT),
      onClick: () => goSettings('members'),
    },
    {
      key: 'history',
      icon: History,
      label: t('nav.history', 'Activity history'),
      sub: t('more_history_sub', 'Your test & activity history'),
      show: true,
      onClick: () => navigate('/app/history'),
    },
    {
      key: 'account',
      icon: Settings,
      label: t('settings.account', 'Account settings'),
      sub: t('more_account_sub', 'Profile, password, sessions'),
      show: true,
      onClick: () => goSettings('profile'),
    },
    {
      key: 'org',
      icon: ShieldCheck,
      label: t('settings.org_roles', 'Org settings & roles'),
      sub: t('more_org_sub', 'General, notifications, permissions'),
      show: has(PERMISSIONS.ROLES_EDIT),
      onClick: () => goSettings('org'),
    },
    {
      key: 'billing',
      icon: CreditCard,
      label: t('settings.billing', 'Billing'),
      sub: t('more_billing_sub', 'Plan, seats, transactions'),
      show: has(PERMISSIONS.BILLING_EDIT),
      onClick: () => goSettings('billing'),
    },
  ];

  const initials =
    user?.name
      ?.split(' ')
      .map((s) => s.charAt(0))
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'U';

  return (
    <MobileScreen>
      <ScreenHeader title={t('more', 'More')} />
      <ScreenBody>
        {/* Workspace / account card — no sidebar on mobile, so this is the entry
            to the profile dialog + a stable spot for the org identity. */}
        <button
          type="button"
          onClick={() => goSettings('profile')}
          className="mb-3 flex w-full items-center gap-3 rounded-lg p-3.5 text-left"
          style={{ background: M.card, border: `1px solid ${M.border}` }}
        >
          <span
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-[13px] font-bold text-white"
            style={{ background: M.accent }}
          >
            {initials}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13.5px] font-bold" style={{ color: M.ink }}>
              {user?.name || t('account', 'Account')}
            </p>
            <p className="truncate text-[11px]" style={{ color: M.ink3 }}>
              {organization?.name}
            </p>
          </div>
          <ChevronRight className="h-4 w-4 flex-shrink-0" style={{ color: M.chevron }} />
        </button>

        {items
          .filter((it) => it.show)
          .map((it) => (
            <button
              key={it.key}
              type="button"
              onClick={it.onClick}
              className="mb-2.5 flex w-full items-center gap-3 rounded-lg p-3 text-left"
              style={{ background: M.card, border: `1px solid ${M.border}` }}
            >
              {/* Neutral tiles: the label carries the meaning, not a colour per row. */}
              <span
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md"
                style={{ background: M.muted, color: M.ink2 }}
              >
                <it.icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-semibold" style={{ color: M.ink }}>
                  {it.label}
                </p>
                <p className="truncate text-[11px]" style={{ color: M.ink3 }}>
                  {it.sub}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 flex-shrink-0" style={{ color: M.chevron }} />
            </button>
          ))}

        <button
          type="button"
          onClick={() => methods.logout()}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg p-3.5 text-[13px] font-semibold"
          style={{ background: M.card, border: `1px solid ${M.border}`, color: M.red }}
        >
          <LogOut className="h-4 w-4" />
          {t('usermenu_signout', 'Sign out')}
        </button>
      </ScreenBody>
    </MobileScreen>
  );
}

export default MobileMore;
